/**
 * Decides whether the running browser is one Syncretic supports.
 *
 * Support is by brand: Google Chrome and Safari pass, everything else gets the
 * banner — Edge, Opera, Samsung Internet, Yandex, Firefox, in-app web views, and
 * on iOS every brand except Safari and Chrome. Each product token after
 * `(KHTML, like Gecko)` must be one Chrome or Safari emits, so an unknown skin
 * fails closed and there is no deny list. Chrome on iOS carries its own grammar
 * because its desktop mode adds `Version/`, which the desktop grammar must keep
 * rejecting or Android WebView passes. A Safari web app on the Home Screen emits
 * neither `Version/` nor `Safari/` and its user agent is byte-identical to a
 * WKWebView's, so only `navigator.standalone` tells them apart. Arc, Brave and
 * Vivaldi send us Chrome's byte-identical user agent and pass; that hole is
 * accepted and cannot be closed from the client. A `userAgentData` brand of
 * `Google Chrome` is sufficient alone and never rejects. jsdom's `jsdom/` token
 * fails, so a bare render under vitest shows the banner.
 */

type BrowserIdentity = Pick<Navigator, 'userAgent'> & {
  userAgentData?: { brands?: ReadonlyArray<{ brand: string }> };
  standalone?: boolean;
};

type TailGrammar = {
  identifying: ReadonlySet<string>;
  permitted: ReadonlySet<string>;
};

const ENGINE_MARKER = '(KHTML, like Gecko) ';

const CHROME_GRAMMAR: TailGrammar = {
  identifying: new Set(['Chrome', 'HeadlessChrome']),
  permitted: new Set(['Mobile', 'Safari']),
};

const CHROME_IOS_GRAMMAR: TailGrammar = {
  identifying: new Set(['CriOS']),
  permitted: new Set(['Version', 'Mobile', 'Safari']),
};

const SAFARI_GRAMMAR: TailGrammar = {
  identifying: new Set(['Version']),
  permitted: new Set(['Mobile', 'Safari']),
};

function matchesTail(tokens: ReadonlyArray<string>, grammar: TailGrammar): boolean {
  return (
    tokens.some((token) => grammar.identifying.has(token)) &&
    tokens.every((token) => grammar.identifying.has(token) || grammar.permitted.has(token))
  );
}

export function isSupportedBrowser(nav: BrowserIdentity = navigator): boolean {
  if (nav.standalone === true) return true;

  const brands = nav.userAgentData?.brands ?? [];
  if (brands.some(({ brand }) => brand === 'Google Chrome')) return true;

  const markerAt = nav.userAgent.indexOf(ENGINE_MARKER);
  if (markerAt === -1) return false;

  const tokens = nav.userAgent
    .slice(markerAt + ENGINE_MARKER.length)
    .split(' ')
    .filter((token) => token !== '')
    .map((token) => token.split('/')[0]);

  return (
    matchesTail(tokens, CHROME_GRAMMAR) ||
    matchesTail(tokens, CHROME_IOS_GRAMMAR) ||
    matchesTail(tokens, SAFARI_GRAMMAR)
  );
}
